'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import isEmpty from 'lodash/isEmpty'

import {
  faClose,
  faExclamationCircle,
  faExclamationTriangle,
  faSpinnerThird,
  faFrown,
  faCircleCheck,
} from '@document-crunch/pro-regular-svg-icons'

import { CrunchData } from '@dc-shared-web/hooks/api/document/types'
import { useSetUserFlag } from '@dc-shared-web/hooks/api/user/useSetUserFlag'

import { useChat } from '@dc-shared-web/providers/ChatProvider'
import {
  ChatErrorType,
  ChatServiceState,
  ChatSettings,
  ChatTextReference,
} from '@dc-shared-web/providers/ChatProvider/types'

import { Button } from '@dc-shared-web/components/shadcn/button'
import { Icon } from '@dc-shared-web/components/base/Icon'
import { AIConfigDialog } from '@dc-shared-web/components/features/AIConfigDialog'
import { AIConfigModalValues } from '@dc-shared-web/components/features/AIConfigDialog/types'
import DebugMenu from '@dc-shared-web/components/features/Chat/DebugMenu'
import ChatInput from '@dc-shared-web/components/features/Chat/ChatInput'
import ChatConversation, {
  ChatConversationRef,
  PageLinkWithHighlightComponent,
} from '@dc-shared-web/components/features/Chat/ChatConversation'
import ChatDisclaimer from '@dc-shared-web/components/features/Chat/ChatDisclaimer'
import {
  ChatLoadingMessage,
  ROBOT_MESSAGE_CLASSNAME,
} from '@dc-shared-web/components/features/Chat/ChatLoadingMessage'
import {
  getAIConfigModalConfigValues,
  getAIConfigModalDefaultValues,
  documentNeedsRecrunch,
} from '@dc-shared-web/components/features/Chat/helpers'
import {
  ERROR_MESSAGES,
  SVC_CHAT_CUTOFF_DATE,
} from '@dc-shared-web/components/features/Chat/constants'
import { useFSTrackEvent } from '@dc-shared-web/hooks'
import { prepareFullStoryEventForCrunchData } from '@dc-shared-web/lib/utils'
import { FS } from '@dc-shared-web/lib/trackingConstants'
import { VCCardProps } from '@dc-shared-web/hooks/api/comparison/types'
import { BaseAlert } from '@dc-shared-web/components/base'
import { AI_TEXT_PAGE_RE } from '@dc-shared-web/components/base/AIText'
import { cn } from '@dc-shared-web/lib'

export const Chat = ({
  crunchData,
  pageLink,
  onExplainSelection,
  useNativeClipboard = false,
  selectedComparison,
  enablePagination = false,
}: {
  crunchData: CrunchData
  pageLink: PageLinkWithHighlightComponent
  onExplainSelection: (references: ChatTextReference[] | undefined) => void
  useNativeClipboard?: boolean
  selectedComparison?: VCCardProps
  enablePagination?: boolean
}) => {
  const {
    hasLoaded,
    isReceiving,
    currentSettings,
    defaultSettings,
    messages,
    serviceStatus,
    errors,
    query,
    stop,
    setCurrentSettings,
    setServiceStatus,
    queryInProgress,
    isAgenticChat,
  } = useChat()

  const { trackEvent } = useFSTrackEvent()

  const [showSuggestions, setShowSuggestions] = useState(false)
  const [chatSettingsOpen, setChatSettingsOpen] = useState(false)
  const [displayedChatSettings, setDisplayedChatSettings] = useState<
    ChatSettings | undefined
  >(undefined)
  const [showAlert, setShowAlert] = useState(false)
  const [showDisclaimer, setShowDisclaimer] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [showCitationsAdded, setShowCitationsAdded] = useState(false)
  const [showCitationsError, setShowCitationsError] = useState(false)
  const [lastQuery, setLastQuery] = useState<string>('')

  const chatConversationRef = useRef<ChatConversationRef>(null)
  const successTimerRef = useRef<NodeJS.Timeout>()
  const citationsAddedTimerRef = useRef<NodeJS.Timeout>()
  const citationsErrorTimerRef = useRef<NodeJS.Timeout>()
  const lastCitationsMessageIdRef = useRef<string | undefined>(undefined)
  const lastCitationsErrorMessageIdRef = useRef<string | undefined>(undefined)
  const hasStartedReceivingRef = useRef<boolean>(false)

  // Check if message text contains page references
  const hasPageReferences = (text: string): boolean => {
    AI_TEXT_PAGE_RE.lastIndex = 0
    return AI_TEXT_PAGE_RE.test(text)
  }

  // Check if crunch was created on or after Jan 1, 2025
  // since that's when we started to use chat agentic instead of the gpt chat service
  const isCrunchAfterSvcChatCutoffDate = useMemo(() => {
    const crunchCreatedDate = new Date(crunchData.createdOn)
    return crunchCreatedDate >= SVC_CHAT_CUTOFF_DATE
  }, [crunchData.createdOn])

  // Track when we start receiving a message (indicating a new query, not history)
  useEffect(() => {
    if (isReceiving || queryInProgress) {
      hasStartedReceivingRef.current = true
    }
  }, [isReceiving, queryInProgress])

  const { mutate: setUserFlag } = useSetUserFlag()

  const alertKey = `comparison-chat-alert-dismissed-${selectedComparison?.version_a_guid}-${selectedComparison?.version_b_guid}`

  const onSelectSuggestion = (suggestion: string) => {
    setLastQuery(suggestion)
    query(suggestion)
    setShowSuggestions(false)

    const fullStoryEvent = prepareFullStoryEventForCrunchData(crunchData)
    trackEvent(FS.CHAT_SUGGESTION, fullStoryEvent)
  }

  const onConfigSubmit = (values: AIConfigModalValues) => {
    setCurrentSettings({
      model_provider_map: currentSettings?.model_provider_map || [],
      context_mode_map: currentSettings?.context_mode_map || {},
      command: 'query',
      context: currentSettings?.context || '',
      prompt: currentSettings?.prompt || '',
      system: values.systemPrompt,
      provider: values.provider,
      model: values.model,
      context_mode: values.contextMode,
      temperature: values.temperature,
      top_p: values.topP,
      provisions: values.provisions,
    })
  }

  const errorMessage = useMemo(() => {
    if (
      documentNeedsRecrunch(errors) ||
      serviceStatus === ChatServiceState.UNAVAILABLE
    ) {
      return ERROR_MESSAGES.unavailable
    }
    if (serviceStatus === ChatServiceState.BUSY) {
      return ERROR_MESSAGES.busy
    }
    if (
      errors.length > 0 &&
      !errors.some(
        (error) => error.type === ChatErrorType.QueryMaxRetriesExceeded
      )
    ) {
      return ERROR_MESSAGES.default
    }
  }, [errors, serviceStatus])

  useEffect(() => {
    if (
      errors.some(
        (error) => error.type === ChatErrorType.QueryMaxRetriesExceeded
      )
    ) {
      setServiceStatus(ChatServiceState.BUSY)
    }
  }, [errors, setServiceStatus])

  useEffect(() => {
    setShowSuggestions(hasLoaded && messages.length === 0)
  }, [hasLoaded, messages])

  useEffect(() => {
    if (crunchData.meta.showAIDisclaimer) {
      setShowDisclaimer(true)
    }
  }, [crunchData])

  useEffect(() => {
    // Only auto-scroll if user is at/near the bottom
    if (!isReceiving && chatConversationRef.current?.isAtBottom()) {
      chatConversationRef.current?.scrollToBottom()
    }
  }, [isReceiving])

  useEffect(() => {
    const hasSeenAlert = localStorage.getItem(alertKey)
    setShowAlert(!hasSeenAlert)
  }, [alertKey])

  const hasStreamingMessage =
    messages.length > 0 &&
    messages[messages.length - 1]?.type === 'robot' &&
    isReceiving

  const currentSubStatus = useMemo(() => {
    if (messages.length > 0) {
      const lastMessage = messages[messages.length - 1]
      if (lastMessage?.type === 'robot') {
        return lastMessage.sub_status
      }
    }
    return undefined
  }, [messages])

  // Monitor for completed messages with citations to show "Citations added" or error message
  // Only show for query messages completed after receiving stops
  useEffect(() => {
    if (
      !hasLoaded ||
      isReceiving ||
      queryInProgress ||
      !isCrunchAfterSvcChatCutoffDate
    ) {
      return
    }

    // Don't show citations messages for messages from history (before we started receiving this session)
    if (!hasStartedReceivingRef.current) {
      return
    }

    // Find the last robot message
    const lastRobotMessage = messages
      .filter((m) => m.type === 'robot')
      .slice(-1)[0]

    if (!lastRobotMessage?.chat_item_id) {
      return
    }

    // Skip if we've already processed this message
    if (
      lastRobotMessage.chat_item_id === lastCitationsMessageIdRef.current ||
      lastRobotMessage.chat_item_id === lastCitationsErrorMessageIdRef.current
    ) {
      return
    }

    // Check if this is a query message (skip explain messages)
    const isQueryMessage =
      (lastRobotMessage.settings as ChatSettings)?.command === 'query' ||
      ((lastRobotMessage.settings as ChatSettings)?.command !== 'explain' &&
        lastRobotMessage.type === 'robot')

    if (!isQueryMessage) {
      return
    }

    // Check if message is completed
    if (!lastRobotMessage.completed) {
      return
    }

    const settings = lastRobotMessage.settings as ChatSettings
    const hasCitations =
      lastRobotMessage.citations &&
      Object.keys(lastRobotMessage.citations).length > 0

    // Check for error conditions first: with_citations is true, has page references, but citations are missing
    if (
      settings?.with_citations &&
      lastRobotMessage.text &&
      hasPageReferences(lastRobotMessage.text) &&
      !hasCitations
    ) {
      lastCitationsErrorMessageIdRef.current = lastRobotMessage.chat_item_id
      setShowCitationsError(true)

      if (citationsErrorTimerRef.current) {
        clearTimeout(citationsErrorTimerRef.current)
      }
      citationsErrorTimerRef.current = setTimeout(() => {
        setShowCitationsError(false)
        citationsErrorTimerRef.current = undefined
      }, 3000)
      return
    }

    // Otherwise, check if message has citations and show success message
    if (hasCitations) {
      lastCitationsMessageIdRef.current = lastRobotMessage.chat_item_id
      setShowCitationsAdded(true)

      if (citationsAddedTimerRef.current) {
        clearTimeout(citationsAddedTimerRef.current)
      }
      citationsAddedTimerRef.current = setTimeout(() => {
        setShowCitationsAdded(false)
        citationsAddedTimerRef.current = undefined
      }, 1000)
    }
  }, [
    messages,
    hasLoaded,
    isReceiving,
    queryInProgress,
    isCrunchAfterSvcChatCutoffDate,
    crunchData.id,
  ])

  // Monitor hasStreamingMessage state to trigger success state when response completes// Only show success if we're not showing citations added
  useEffect(() => {
    if (hasStreamingMessage && !showCitationsAdded) {
      // Response just completed (transitioned from receiving to not receiving)
      setIsSuccess(true)

      // Clear success state after 1 seconds
      if (!successTimerRef.current) {
        successTimerRef.current = setTimeout(() => {
          setIsSuccess(false)
          successTimerRef.current = undefined
        }, 1000)
      }
    }
  }, [hasStreamingMessage, showCitationsAdded])

  const onChatSubmit = (text: string) => {
    const fullStoryEvent = prepareFullStoryEventForCrunchData(crunchData)
    trackEvent(FS.CHAT_QUERY, fullStoryEvent)

    setLastQuery(text)
    chatConversationRef.current?.scrollToBottom()
    query(text)
  }

  const onRetry = () => {
    if (lastQuery) {
      const fullStoryEvent = prepareFullStoryEventForCrunchData(crunchData)
      trackEvent(FS.CHAT_QUERY, fullStoryEvent)

      chatConversationRef.current?.scrollToBottom()
      query(lastQuery)
    }
  }

  const onAcknowledgeDisclaimer = () => {
    setShowDisclaimer(false)

    if (crunchData.meta.showAIDisclaimer) {
      setUserFlag({
        flag: 'ai_disclaimer',
      })
    }
  }

  const dismissAlert = () => {
    setShowAlert(false)
    localStorage.setItem(alertKey, 'true')
  }

  return (
    <div
      className='tw-w-full tw-h-full tw-flex tw-flex-col tw-overflow-x-hidden'
      data-testid='chat-container'
    >
      <DebugMenu />
      {selectedComparison && showAlert && (
        <div className='tw-p-2 tw-relative'>
          <BaseAlert
            variant='warning'
            icon={faExclamationCircle}
            title='Note'
            description='Chat responses will be based on the version of the document you are viewing.'
          />
          <Button
            size='sm'
            variant='ghost'
            onClick={dismissAlert}
            className='tw-absolute tw-top-2 tw-right-2 hover:tw-bg-transparent'
            aria-label='Dismiss alert'
          >
            <Icon icon={faClose} />
          </Button>
        </div>
      )}
      <ChatConversation
        ref={chatConversationRef}
        crunchData={crunchData}
        showSuggestions={showSuggestions}
        pageLink={pageLink}
        onOpenSettings={(settings) => {
          setDisplayedChatSettings(
            isEmpty(settings) ? currentSettings : (settings as ChatSettings)
          )
          setChatSettingsOpen(true)
        }}
        onSelectSuggestion={onSelectSuggestion}
        onShowExplainSelection={onExplainSelection}
        useNativeClipboard={useNativeClipboard}
        showMostRecentMessage={!isSuccess}
        enablePagination={enablePagination}
      />
      {!hasLoaded && (
        <div className='tw-flex tw-gap-2 tw-items-center tw-px-3 tw-py-1'>
          <Icon icon={faSpinnerThird} size='sm' spin />
          <span className='tw-text-sm tw-text-neutral-300'>
            Loading chat history...
          </span>
        </div>
      )}
      <ChatLoadingMessage
        isReceiving={isReceiving || queryInProgress}
        isSuccess={isSuccess}
        onStop={stop}
        hasStreamingMessage={hasStreamingMessage}
        subStatus={currentSubStatus}
      />
      {showCitationsAdded && isCrunchAfterSvcChatCutoffDate && (
        <div
          data-testid='chat-citations-added-message'
          className={cn(ROBOT_MESSAGE_CLASSNAME, 'tw-mt-1')}
        >
          <div className='tw-flex tw-justify-between tw-items-center tw-relative tw-z-20 tw-min-h-[40px]'>
            <div className='tw-flex tw-gap-2 tw-items-center tw-min-w-0 tw-flex-1'>
              <div className='tw-flex-shrink-0'>
                <Icon
                  icon={faCircleCheck}
                  size='lg'
                  className='tw-text-neutral-500'
                />
              </div>
              <div className='tw-flex tw-flex-col tw-gap-1 tw-min-w-0'>
                <span className='tw-text-neutral-700 tw-font-medium tw-py-0.5'>
                  Citations added
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCitationsError && isCrunchAfterSvcChatCutoffDate && (
        <div
          data-testid='chat-citations-error-message'
          className={cn(ROBOT_MESSAGE_CLASSNAME, 'tw-mt-1')}
        >
          <div className='tw-flex tw-justify-between tw-items-center tw-relative tw-z-20 tw-min-h-[40px]'>
            <div className='tw-flex tw-gap-2 tw-items-center tw-min-w-0 tw-flex-1'>
              <div className='tw-flex-shrink-0'>
                <Icon
                  icon={faExclamationTriangle}
                  size='lg'
                  className='tw-text-neutral-500'
                />
              </div>
              <div className='tw-flex tw-flex-col tw-gap-1 tw-min-w-0'>
                <span className='tw-text-neutral-700 tw-font-medium tw-py-0.5'>
                  Error processing citations
                </span>
              </div>
            </div>
          </div>
        </div>
      )}
      {hasLoaded && !isReceiving && errorMessage && (
        <div className='tw-p-3 tw-flex tw-items-start tw-gap-3'>
          <Icon
            icon={faFrown}
            className='tw-text-red-500 tw-mt-0.5'
            size='sm'
          />
          <div className='tw-flex tw-flex-col tw-gap-2'>
            <span className='tw-text-sm tw-text-gray-800'>{errorMessage}</span>
            {lastQuery && (
              <Button
                variant='link'
                size='sm'
                onClick={onRetry}
                className='tw-p-0 tw-h-auto tw-text-blue-600 tw-font-normal tw-text-sm tw-self-start'
              >
                Retry
              </Button>
            )}
          </div>
        </div>
      )}
      <div className='tw-basis-20 tw-mt-2 tw-bg-white tw-z-10'>
        <ChatInput
          isReceiving={isReceiving || queryInProgress}
          showSuggestions={showSuggestions}
          onSubmit={onChatSubmit}
          onOpenSettings={() => {
            setDisplayedChatSettings(undefined)
            setChatSettingsOpen(true)
          }}
          onToggleSuggestions={() => setShowSuggestions(!showSuggestions)}
        />
        <div className='tw-flex tw-items-center tw-justify-center tw-h-[40px] tw-overflow-hidden tw-truncate'>
          <span className='tw-text-neutral-300 tw-text-[12px] tw-h-fit'>
            Chat can make mistakes. Refer to our
          </span>
          <Button
            variant='link'
            className='tw-p-0 tw-h-[16px] tw-ml-1 tw-text-neutral-300 tw-text-[12px] tw-underline tw-font-normal'
            onClick={() => setShowDisclaimer(true)}
            data-testid='chat-disclaimer-button'
          >
            disclaimer
          </Button>
        </div>
      </div>
      <AIConfigDialog
        open={chatSettingsOpen}
        values={getAIConfigModalConfigValues(
          displayedChatSettings,
          currentSettings,
          defaultSettings
        )}
        providers={defaultSettings?.model_provider_map || []}
        defaultValues={getAIConfigModalDefaultValues(defaultSettings)}
        onClose={() => setChatSettingsOpen(false)}
        onSubmit={onConfigSubmit}
        editable={!displayedChatSettings}
        isAgenticChat={isAgenticChat}
      />
      <ChatDisclaimer
        open={showDisclaimer}
        acknowledged={!crunchData.meta.showAIDisclaimer}
        onClose={onAcknowledgeDisclaimer}
      />
    </div>
  )
}
